import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Pagination, Spin } from "antd";

import { Handbook } from "features/handbook/model/handbook";
import useMediaQuery from "shared/hooks/useMediaQuery";
import { buildHandbookColumns } from "./handbookColumns";
import {
  HandbookColumnFlags,
  HandbookRefFields,
} from "./handbookColumns.types";
import { HandbookTableRow } from "./HandbookTableRow";
import styles from "./HandbookTable.module.css";

type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

type Props = {
  data: Handbook[];
  isFetching?: boolean;
  pagination?: PaginationState;
  onPageChange?: (page: number, pageSize: number) => void;
  onEdit?: (row: Handbook) => void;
  onDelete?: (row: Handbook) => void;
  /** Какие колонки показывать — выводится из swagger-схемы справочника. */
  flags: HandbookColumnFlags;
  /** Имена полей-ссылок в данных (`animalCategory`, `greenhouseCrop`). */
  refFields: HandbookRefFields;
  showAttachButton?: boolean;
  onAttachToService?: (row: Handbook) => void;
  showDetachButton?: boolean;
  onDetachFromService?: (row: Handbook) => void;
  attachedIds?: number[];
  isAgroCredit?: boolean;
};

/** №, название RU и название KY есть всегда; за ними — колонка действий на 2 ячейки. */
const FIXED_COLS = 3;
const ACTION_COLS = 2;

export const HandbookTable = ({
  data,
  isFetching,
  pagination,
  onPageChange,
  attachedIds,
  flags,
  refFields,
  isAgroCredit,
  ...rowActions
}: Props) => {
  const { t } = useTranslation();
  const isScreensLg = useMediaQuery("(min-width: 768px)");

  const attachedSet = useMemo(() => new Set(attachedIds ?? []), [attachedIds]);

  const columns = buildHandbookColumns({ t, refFields, isAgroCredit, flags });

  const totalCols = FIXED_COLS + columns.length + ACTION_COLS;
  const pageOffset =
    ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? data.length);

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table} role="table">
        <thead className={styles.thead}>
          <tr>
            <th style={{ width: 56 }}>№</th>
            <th>{t("handbook.table.nameRu")}</th>
            <th>{t("handbook.table.nameKy")}</th>

            {columns.map((column) => (
              <th key={column.key}>{column.header}</th>
            ))}

            <th className={styles.actionsHead} colSpan={ACTION_COLS}>
              {t("handbook.table.action")}
            </th>
          </tr>
        </thead>

        <tbody>
          {isFetching && (
            <tr>
              <td className={styles.spinnerCell} colSpan={totalCols}>
                <Spin spinning />
              </td>
            </tr>
          )}

          {!isFetching && data?.length > 0
            ? data.map((item, idx) => (
                <HandbookTableRow
                  key={item.id ?? `row-${idx}`}
                  row={item}
                  index={idx + 1 + pageOffset}
                  columns={columns}
                  isAttached={item.id != null && attachedSet.has(item.id)}
                  showActionButtons={isScreensLg}
                  {...rowActions}
                />
              ))
            : !isFetching && (
                <tr>
                  <td className={styles.emptyState} colSpan={totalCols}>
                    {t("handbook.table.nothingFound") ?? "Ничего не найдено"}
                  </td>
                </tr>
              )}
        </tbody>
      </table>

      {pagination && (
        <div className={styles.paginationBar}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            responsive
            onChange={(page, size) => onPageChange?.(page, size)}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
          />
        </div>
      )}
    </div>
  );
};
